import React from 'react';
import { Alert, Box, Chip, Stack, Typography, alpha, useTheme } from '@mui/material';
import SalesOpsHeader from './SalesOpsHeader';
import {
  BarListSkeleton,
  Card,
  MethodNote,
  Shimmer,
  StatBar,
  TableSkeleton,
  Tile,
  count,
  num,
  pct,
  tableSx,
} from './salesOpsKit';
import { fetchSalesOpsToday } from '../../services/salesOperationsService';
import type { SalesOpsToday } from '../../types/salesOperations';

/**
 * Today -- the operating picture (handover 2.3). "Not a report."
 *
 * The distinction matters for positioning: a dashboard makes this analytics,
 * a decision engine makes it operations. So the feed says what the system
 * DID, and the alerts carry the number that makes each one actionable.
 */
export default function SalesOpsTodayPage() {
  const [data, setData] = React.useState<SalesOpsToday | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);

  const load = React.useCallback((force = false) => {
    const controller = new AbortController();
    setLoading(true);
    fetchSalesOpsToday(controller.signal, force)
      .then((d) => { setData(d); setError(null); })
      .catch((e: Error) => { if (e.name !== 'AbortError') setError(e.message); })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, []);

  React.useEffect(() => load(), [load]);

  const t = data?.tiles;
  const pending = loading || !data;
  const short = t && t.scheduled_fte !== null && t.required_fte !== null
    && t.scheduled_fte < t.required_fte;

  return (
    <Box>
      <SalesOpsHeader
        title="Today"
        description="What the operation is doing right now, and what needs a person"
        asOf={data?.anchor_date}
        computedAt={data?.computed_at}
        fromCache={data?.from_cache}
        onRefresh={() => load(true)}
        refreshing={loading}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!error && (
        <Stack spacing={2}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
              gap: 1.75,
            }}
          >
            <Tile
              loading={pending}
              label="Leads in queue"
              value={count(t?.leads_in_queue)}
              detail={`${count(t?.arrived_today)} arrived today`}
              tooltip="Leads that are unsold, inside the callable age window, and still have attempts left in their cadence."
            />
            <Tile
              loading={pending}
              label="Contact rate"
              value={pct(t?.contact_rate)}
              detail={`baseline ${pct(t?.baseline_contact_rate)}`}
              tone={
                t && t.contact_rate !== null && t.baseline_contact_rate !== null
                  ? t.contact_rate >= t.baseline_contact_rate ? 'good' : 'warn'
                  : 'neutral'
              }
              tooltip={data?.contact_rate_definition}
            />
            <Tile
              loading={pending}
              label="Capacity"
              value={`${num(t?.scheduled_fte)} / ${num(t?.required_fte)}`}
              detail="scheduled vs required FTE"
              tone={short ? 'warn' : 'neutral'}
              tooltip="Required FTE is derived from this month's target — see Plan for the full derivation."
            />
            <Tile
              loading={pending}
              label="Decisions made"
              value={count(t?.decisions_today)}
              detail="dials placed today"
            />
          </Box>

          <Card title="Needs attention" dense>
            {pending ? (
              <TableSkeleton rows={3} cols={3} />
            ) : (
            <Box component="table" sx={tableSx}>
              <tbody>
                {data?.attention.length === 0 && (
                  <tr><td style={{ color: 'var(--mui-palette-text-disabled)' }}>
                    Nothing flagged.
                  </td></tr>
                )}
                {data?.attention.map((a, i) => (
                  <Box
                    component="tr"
                    key={i}
                    sx={(th) => ({
                      borderTop: i === 0 ? 'none'
                        : `1px solid ${th.tokens?.border?.subtle ?? th.palette.divider}`,
                    })}
                  >
                    <td style={{ width: 130 }}>
                      <Chip
                        label={a.kind}
                        size="small"
                        sx={(th) => ({
                          height: 20,
                          fontSize: 11,
                          bgcolor: alpha(th.palette.warning.main, 0.14),
                          color: th.palette.warning.main,
                        })}
                      />
                    </td>
                    <td>{a.message}</td>
                    <td className="num" style={{ color: 'var(--mui-palette-text-secondary)' }}>
                      {a.impact}
                    </td>
                  </Box>
                ))}
              </tbody>
            </Box>
            )}
          </Card>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 2,
              alignItems: 'start',
            }}
          >
            <Card title="What changed today" dense>
              {pending ? <TableSkeleton rows={3} cols={1} /> : (
              <Box component="table" sx={tableSx}>
                <tbody>
                  {data?.changes.length === 0 && (
                    <tr><td style={{ color: 'var(--mui-palette-text-disabled)' }}>
                      Nothing recorded yet today.
                    </td></tr>
                  )}
                  {data?.changes.map((c, i) => (
                    <Box
                      component="tr"
                      key={i}
                      sx={(th) => ({
                        borderTop: i === 0 ? 'none'
                          : `1px solid ${th.tokens?.border?.subtle ?? th.palette.divider}`,
                      })}
                    >
                      <td>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 600, mb: 0.25 }}>
                          {c.headline}
                        </Typography>
                        <Typography sx={{ fontSize: 12.5, color: 'text.secondary', lineHeight: 1.5 }}>
                          {c.detail}
                        </Typography>
                      </td>
                    </Box>
                  ))}
                </tbody>
              </Box>
              )}
            </Card>

            <Card title="Contact rate">
              {pending ? <BarListSkeleton rows={3} /> : <ContactRateComparison data={data!} />}
              {pending ? (
                <Shimmer width="90%" height={12} sx={{ mt: 1.5 }} />
              ) : (
                <MethodNote>
                  {data!.contact_rate_definition} The target is a business goal, not a
                  measurement — most of the distance to it is capacity rather than
                  ranking, and closing it needs attempts the floor has no hours for.
                </MethodNote>
              )}
            </Card>
          </Box>

        </Stack>
      )}
    </Box>
  );
}

/** The stated goal for contact rate. A target, and labelled as one. */
const CONTACT_RATE_TARGET = 0.30;

/**
 * Where contact rate is, where it was, and where it is meant to be.
 *
 * Three bars rather than a per-week list: the question this panel answers is
 * "is this working", and eight near-identical weekly bars made the reader do
 * that comparison themselves. The weekly series is still shown, as a
 * sparkline underneath, because the shape of it is worth a glance.
 */
function ContactRateComparison({ data }: { data: SalesOpsToday }) {
  const t = data.tiles;
  const max = CONTACT_RATE_TARGET;
  const points = data.trend.filter((p) => p.contact_rate !== null);
  return (
    <Stack spacing={2}>
      <StatBar
        label="Last 30 days"
        value={t.contact_rate}
        display={pct(t.contact_rate)}
        max={max}
        tone="accent"
      />
      <StatBar
        label="Prior period"
        sublabel="30–180 days"
        value={t.baseline_contact_rate}
        display={pct(t.baseline_contact_rate)}
        max={max}
      />
      <StatBar
        label="Target"
        sublabel="if capacity weren't the constraint"
        value={CONTACT_RATE_TARGET}
        display={`~${Math.round(CONTACT_RATE_TARGET * 100)}%`}
        max={max}
        tone="warn"
        striped
      />
      {points.length > 2 && (
        <Box sx={{ pt: 0.5 }}>
          <Typography sx={{ fontSize: 11.5, color: 'text.disabled', mb: 0.75 }}>
            By week · {points.length} weeks to {points[points.length - 1].week_start}
          </Typography>
          <Sparkline values={points.map((p) => p.contact_rate ?? 0)} />
        </Box>
      )}
    </Stack>
  );
}

/** Weekly shape at a glance. Plain SVG — no axes worth drawing at this size. */
function Sparkline({ values }: { values: number[] }) {
  const width = 260;
  const height = 34;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const step = width / (values.length - 1);
  const points = values.map((v, i) => {
    const x = i * step;
    const y = height - ((v - min) / range) * (height - 6) - 3;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  return (
    <Box sx={{ width: '100%', maxWidth: width, height }}>
      <svg width="100%" height="100%" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
        <SparkPath d={`M${points.join(' L')}`} />
      </svg>
    </Box>
  );
}

function SparkPath({ d }: { d: string }) {
  const theme = useTheme();
  return (
    <path
      d={d}
      fill="none"
      stroke={theme.palette.primary.main}
      strokeWidth={1.75}
      strokeLinejoin="round"
      strokeLinecap="round"
      vectorEffect="non-scaling-stroke"
    />
  );
}
